import { useState, useEffect, useRef } from "react";
import { Play, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchVoicePreview } from "@/api/client";

const ELEVEN_LABS_VOICES = [
  { id: "5l4ttmr4SKNgi0HnOelT", name: "Paul K", descriptor: "Deep French Narrator – Confident, middle-aged, FR" },
  { id: "flHkNRp1BlvT73UL6gyz", name: "Jessica Anne Bogart", descriptor: "Character & Animation – Crisp, middle-aged, US" },
  { id: "jK7dAsiVAhbApIS8KkWB", name: "Vincent (JC)", descriptor: "Smooth, classy, middle-aged, FR" },
  { id: "NOpBlnGInO9m6vDvFkFC", name: "Grandpa Spuds Oxley", descriptor: "Friendly grandpa – Gentle, older, US" },
  { id: "jUHQdLfy668sllNiNTSW", name: "Clément", descriptor: "Top Voice France – Calm, middle-aged, FR" },
  { id: "tKaoyJLW05zqV0tIH9FD", name: "Gaëlle", descriptor: "Audiobooks & Storytelling – Warm, middle-aged, FR" },
  { id: "T4BwQ2ZwlS2BbHIfci4H", name: "Souni", descriptor: "Gentle French female – Calm, young, FR" },
  { id: "GYzIdoKkRyANjBvkKYfO", name: "Koraly", descriptor: "Smooth & Captivating – Pro voice clone, FR" },
  { id: "TojRWZatQyy9dujEdiQ1", name: "Koraly (Storyteller)", descriptor: "Storyteller – Audiobook-tuned, FR" },
];

type VoiceGridPickerProps = {
  selectedVoiceId: string | null;
  onSelect: (voiceId: string) => void;
};

export function VoiceGridPicker({ selectedVoiceId, onSelect }: VoiceGridPickerProps) {
  const [voicePreviewUrls, setVoicePreviewUrls] = useState<Record<string, string>>({});
  const [voicePreviewLoading, setVoicePreviewLoading] = useState<Record<string, boolean>>({});
  const [voicePreviewErrors, setVoicePreviewErrors] = useState<Record<string, string | null>>({});

  const voicePreviewUrlsRef = useRef(voicePreviewUrls);
  useEffect(() => { voicePreviewUrlsRef.current = voicePreviewUrls; }, [voicePreviewUrls]);
  useEffect(() => () => { Object.values(voicePreviewUrlsRef.current).forEach(URL.revokeObjectURL); }, []);

  const handlePreviewVoice = async (voiceId: string) => {
    setVoicePreviewErrors((p) => ({ ...p, [voiceId]: null }));
    setVoicePreviewLoading((p) => ({ ...p, [voiceId]: true }));
    try {
      const blob = await fetchVoicePreview(voiceId);
      const url = URL.createObjectURL(blob);
      setVoicePreviewUrls((p) => {
        if (p[voiceId]) URL.revokeObjectURL(p[voiceId]);
        return { ...p, [voiceId]: url };
      });
    } catch (err) {
      setVoicePreviewErrors((p) => ({ ...p, [voiceId]: (err as Error).message }));
    } finally {
      setVoicePreviewLoading((p) => ({ ...p, [voiceId]: false }));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Voix ElevenLabs</CardTitle>
        <CardDescription>Écoutez un extrait et sélectionnez la voix narratrice.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {ELEVEN_LABS_VOICES.map((voice) => {
            const isSelected = selectedVoiceId === voice.id;
            return (
              <div
                key={voice.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(voice.id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(voice.id); } }}
                className={cn(
                  "flex flex-col gap-2 rounded-xl border p-3 cursor-pointer transition-all",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isSelected ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/40"
                )}
              >
                <input type="radio" name="voice-choice" checked={isSelected} readOnly className="sr-only" />
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold bg-primary/10 text-primary rounded-full px-2 py-0.5">
                    {voice.name}
                  </span>
                  <span className={cn(
                    "h-3 w-3 rounded-full border-2 transition-colors",
                    isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                  )} />
                </div>
                <p className="text-[10px] text-muted-foreground leading-tight">{voice.descriptor}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  disabled={Boolean(voicePreviewLoading[voice.id])}
                  onClick={(e) => { e.stopPropagation(); handlePreviewVoice(voice.id); }}
                >
                  {voicePreviewLoading[voice.id] ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Play className="h-3 w-3 mr-1" />
                  )}
                  Écouter
                </Button>
                {voicePreviewErrors[voice.id] && (
                  <p className="text-xs text-destructive">{voicePreviewErrors[voice.id]}</p>
                )}
                {voicePreviewUrls[voice.id] && (
                  <audio controls src={voicePreviewUrls[voice.id]} preload="none" className="w-full" onClick={(e) => e.stopPropagation()} />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
